/**
 * Serialize / parse Inventory page (certified + lots) filters for the browser URL.
 * Certified uses API-aligned names; lots use a `lot_` prefix to avoid clashes.
 */

/** Stable string for comparing two URLSearchParams regardless of insertion order. */
export function serializeSearchParamsSorted(params) {
  const p = params instanceof URLSearchParams ? params : new URLSearchParams(params);
  return [...p.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
}

const CERTIFIED_PAGE_LIMITS = new Set([10, 20, 50, 100]);
const LOTS_PAGE_LIMITS = new Set([10, 25, 50, 100]);

/** Certified inventory column ids accepted by GET /api/diamonds sortBy. */
export const CERTIFIED_API_SORT_FIELDS = new Set([
  'price', 'carat', 'createdAt', 'updatedAt',
]);

export const INTERNAL_ONLY_CERTIFIED_SORT_COLUMNS = new Set(['dateAdded', 'dateUpdated']);

export const DEFAULT_INTERNAL_CERTIFIED_SORT = 'dateAdded';
export const DEFAULT_CLIENT_CERTIFIED_SORT = 'price';

/** Map UI column id to diamonds API sortBy param. */
export function certifiedSortColumnToApiField(columnId) {
  if (columnId === 'dateAdded') return 'createdAt';
  if (columnId === 'dateUpdated') return 'updatedAt';
  return columnId;
}

/** Whether a UI column id is supported by the diamonds list API for the current user. */
export function isCertifiedSortColumn(columnId, isInternal = true) {
  if (!isInternal && INTERNAL_ONLY_CERTIFIED_SORT_COLUMNS.has(columnId)) return false;
  return CERTIFIED_API_SORT_FIELDS.has(certifiedSortColumnToApiField(columnId));
}

/** Normalize certified sort column from URL or user action. */
export function normalizeCertifiedSortColumn(columnId, isInternal = true) {
  let sortBy = columnId || (isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT);
  if (sortBy === 'createdAt') sortBy = 'dateAdded';
  if (sortBy === 'updatedAt') sortBy = 'dateUpdated';
  if (sortBy === 'totalPrice' || sortBy === 'ratio') {
    sortBy = isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT;
  }
  if (!isInternal && INTERNAL_ONLY_CERTIFIED_SORT_COLUMNS.has(sortBy)) {
    return DEFAULT_CLIENT_CERTIFIED_SORT;
  }
  if (!isCertifiedSortColumn(sortBy, isInternal)) {
    return isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT;
  }
  return sortBy;
}

/** Non-certified lots column ids accepted by GET /api/non-certified-diamonds. */
export const LOTS_API_SORT_FIELDS = new Set([
  'shape', 'length', 'caratPerStone', 'pricePerCarat', 'availableCarat',
]);

export function isLotsSortColumn(columnId) {
  return LOTS_API_SORT_FIELDS.has(columnId);
}

function splitCsv(s) {
  if (s == null || s === '') return [];
  return String(s)
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/**
 * @param {URLSearchParams} searchParams
 * @param {boolean} isInternal
 */
export function parseInventorySearchParams(searchParams, isInternal) {
  const get = (k) => searchParams.get(k);
  const tabRaw = get('tab');
  const tab = tabRaw === 'lots' || tabRaw === '1' ? 1 : 0;

  let status = ['available'];
  if (isInternal) {
    const st = get('status');
    if (st === '__all__') status = [];
    else if (st) status = splitCsv(st);
  }

  const filters = {
    status,
    shape: get('shape') || '',
    color: splitCsv(get('color')),
    fancyIntensity: splitCsv(get('fancyIntensity')),
    fancyHue: splitCsv(get('fancyHue')),
    fancyOvertone: splitCsv(get('fancyOvertone')),
    clarity: splitCsv(get('clarity')),
    minCarat: get('minCarat') || '',
    maxCarat: get('maxCarat') || '',
    sku: get('sku') || '',
    certificateNumber: isInternal ? (get('certificateNumber') || '') : '',
    shipmentId: isInternal ? (get('shipmentId') || '') : '',
    shipmentItemID: isInternal ? (get('shipmentItemID') || '') : '',
    cut: get('cut') || '',
    polish: get('polish') || '',
    symmetry: get('symmetry') || '',
  };

  const advancedFilters = {
    minRatio: get('minRatio') || '',
    maxRatio: get('maxRatio') || '',
    minLength: get('minLength') || '',
    maxLength: get('maxLength') || '',
    minWidth: get('minWidth') || '',
    maxWidth: get('maxWidth') || '',
  };

  const sortBy = normalizeCertifiedSortColumn(get('sortBy'), isInternal);
  const sortOrder = get('sortOrder') === 'asc' ? 'asc' : 'desc';

  let page = parseInt(get('page'), 10);
  page = Number.isFinite(page) ? Math.max(0, page - 1) : 0;

  const limit = parseInt(get('limit'), 10);
  const rowsPerPage = Number.isFinite(limit) && CERTIFIED_PAGE_LIMITS.has(limit) ? limit : null;

  let lotsPage = parseInt(get('lot_page'), 10);
  lotsPage = Number.isFinite(lotsPage) ? Math.max(0, lotsPage - 1) : 0;

  const lotLimit = parseInt(get('lot_limit'), 10);
  const lotsRowsPerPage = Number.isFinite(lotLimit) && LOTS_PAGE_LIMITS.has(lotLimit) ? lotLimit : null;

  const lotsAvailableOnly = get('lot_show') !== 'all';

  return {
    tab,
    filters,
    advancedFilters,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    lotsPage,
    lotsRowsPerPage,
    lots: {
      shape: get('lot_shape') || '',
      availableOnly: lotsAvailableOnly,
      minCaratPerStone: get('lot_minCt') || '',
      maxCaratPerStone: get('lot_maxCt') || '',
      minLength: get('lot_minLen') || '',
      maxLength: get('lot_maxLen') || '',
      minWidth: get('lot_minW') || '',
      maxWidth: get('lot_maxW') || '',
    },
  };
}

function appendIfTruthy(p, key, value) {
  if (value === '' || value == null) return;
  if (Array.isArray(value) && value.length === 0) return;
  if (Array.isArray(value)) p.set(key, value.join(','));
  else p.set(key, String(value));
}

/**
 * @param {object} snapshot — current Inventory UI state used to build the query string
 */
export function buildInventorySearchParams(snapshot) {
  const {
    inventoryTab,
    filters,
    advancedFilters,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    lotsPage,
    lotsRowsPerPage,
    lotsFilterShape,
    lotsAvailableOnly,
    lotsMinCaratPerStone,
    lotsMaxCaratPerStone,
    lotsMinLength,
    lotsMaxLength,
    lotsMinWidth,
    lotsMaxWidth,
    isInternal,
    defaultCertifiedRows = 50,
  } = snapshot;

  const p = new URLSearchParams();

  if (inventoryTab === 1) {
    p.set('tab', 'lots');
  }

  if (inventoryTab === 0) {
    if (isInternal) {
      if (filters.status.length === 0) {
        p.set('status', '__all__');
      } else if (filters.status.length !== 1 || filters.status[0] !== 'available') {
        p.set('status', filters.status.join(','));
      }
    }

    appendIfTruthy(p, 'shape', filters.shape);
    appendIfTruthy(p, 'color', filters.color);
    appendIfTruthy(p, 'fancyIntensity', filters.fancyIntensity);
    appendIfTruthy(p, 'fancyHue', filters.fancyHue);
    appendIfTruthy(p, 'fancyOvertone', filters.fancyOvertone);
    appendIfTruthy(p, 'clarity', filters.clarity);
    appendIfTruthy(p, 'minCarat', filters.minCarat);
    appendIfTruthy(p, 'maxCarat', filters.maxCarat);
    appendIfTruthy(p, 'sku', filters.sku);
    if (isInternal) {
      appendIfTruthy(p, 'certificateNumber', filters.certificateNumber);
      appendIfTruthy(p, 'shipmentId', filters.shipmentId);
      appendIfTruthy(p, 'shipmentItemID', filters.shipmentItemID);
    }
    appendIfTruthy(p, 'cut', filters.cut);
    appendIfTruthy(p, 'polish', filters.polish);
    appendIfTruthy(p, 'symmetry', filters.symmetry);

    appendIfTruthy(p, 'minRatio', advancedFilters.minRatio);
    appendIfTruthy(p, 'maxRatio', advancedFilters.maxRatio);
    appendIfTruthy(p, 'minLength', advancedFilters.minLength);
    appendIfTruthy(p, 'maxLength', advancedFilters.maxLength);
    appendIfTruthy(p, 'minWidth', advancedFilters.minWidth);
    appendIfTruthy(p, 'maxWidth', advancedFilters.maxWidth);

    if (page > 0) p.set('page', String(page + 1));
    if (rowsPerPage != null && rowsPerPage !== defaultCertifiedRows) {
      p.set('limit', String(rowsPerPage));
    }

    const defaultSort = isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT;
    if (sortBy && sortBy !== defaultSort) p.set('sortBy', sortBy);
    if (sortOrder && sortOrder !== 'desc') p.set('sortOrder', sortOrder);
  }

  if (inventoryTab === 1) {
    appendIfTruthy(p, 'lot_shape', lotsFilterShape);
    if (!lotsAvailableOnly) p.set('lot_show', 'all');
    appendIfTruthy(p, 'lot_minCt', lotsMinCaratPerStone);
    appendIfTruthy(p, 'lot_maxCt', lotsMaxCaratPerStone);
    appendIfTruthy(p, 'lot_minLen', lotsMinLength);
    appendIfTruthy(p, 'lot_maxLen', lotsMaxLength);
    appendIfTruthy(p, 'lot_minW', lotsMinWidth);
    appendIfTruthy(p, 'lot_maxW', lotsMaxWidth);

    if (lotsPage > 0) p.set('lot_page', String(lotsPage + 1));
    if (lotsRowsPerPage != null && lotsRowsPerPage !== 10) {
      p.set('lot_limit', String(lotsRowsPerPage));
    }
  }

  return p;
}

// ─────────────────────────────────────────────────────────────────────────────
// Certified-route helpers  (/inventory/certified)
// ─────────────────────────────────────────────────────────────────────────────

/** Parse certified filter/sort/pagination from a /inventory/certified search string. */
export function parseCertifiedSearchParams(searchParams, isInternal) {
  const get = (k) => searchParams.get(k);

  let status = ['available'];
  if (isInternal) {
    const st = get('status');
    if (st === '__all__') status = [];
    else if (st) status = splitCsv(st);
  }

  const filters = {
    status,
    shape: get('shape') || '',
    color: splitCsv(get('color')),
    fancyIntensity: splitCsv(get('fancyIntensity')),
    fancyHue: splitCsv(get('fancyHue')),
    fancyOvertone: splitCsv(get('fancyOvertone')),
    clarity: splitCsv(get('clarity')),
    minCarat: get('minCarat') || '',
    maxCarat: get('maxCarat') || '',
    sku: get('sku') || '',
    certificateNumber: isInternal ? (get('certificateNumber') || '') : '',
    shipmentId: isInternal ? (get('shipmentId') || '') : '',
    shipmentItemID: isInternal ? (get('shipmentItemID') || '') : '',
    cut: get('cut') || '',
    polish: get('polish') || '',
    symmetry: get('symmetry') || '',
  };

  const advancedFilters = {
    minRatio: get('minRatio') || '',
    maxRatio: get('maxRatio') || '',
    minLength: get('minLength') || '',
    maxLength: get('maxLength') || '',
    minWidth: get('minWidth') || '',
    maxWidth: get('maxWidth') || '',
  };

  const sortBy = normalizeCertifiedSortColumn(get('sortBy'), isInternal);
  const sortOrder = get('sortOrder') === 'asc' ? 'asc' : 'desc';

  let page = parseInt(get('page'), 10);
  page = Number.isFinite(page) ? Math.max(0, page - 1) : 0;

  const limit = parseInt(get('limit'), 10);
  const rowsPerPage = Number.isFinite(limit) && CERTIFIED_PAGE_LIMITS.has(limit) ? limit : null;

  return { filters, advancedFilters, sortBy, sortOrder, page, rowsPerPage };
}

/** Build URLSearchParams for /inventory/certified route. */
export function buildCertifiedSearchParams(snapshot) {
  const {
    filters,
    advancedFilters,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    isInternal,
    defaultRows = 50,
  } = snapshot;

  const p = new URLSearchParams();

  if (isInternal) {
    if (filters.status.length === 0) {
      p.set('status', '__all__');
    } else if (filters.status.length !== 1 || filters.status[0] !== 'available') {
      p.set('status', filters.status.join(','));
    }
  }

  appendIfTruthy(p, 'shape', filters.shape);
  appendIfTruthy(p, 'color', filters.color);
  appendIfTruthy(p, 'fancyIntensity', filters.fancyIntensity);
  appendIfTruthy(p, 'fancyHue', filters.fancyHue);
  appendIfTruthy(p, 'fancyOvertone', filters.fancyOvertone);
  appendIfTruthy(p, 'clarity', filters.clarity);
  appendIfTruthy(p, 'minCarat', filters.minCarat);
  appendIfTruthy(p, 'maxCarat', filters.maxCarat);
  appendIfTruthy(p, 'sku', filters.sku);
  if (isInternal) {
    appendIfTruthy(p, 'certificateNumber', filters.certificateNumber);
    appendIfTruthy(p, 'shipmentId', filters.shipmentId);
  }
  appendIfTruthy(p, 'cut', filters.cut);
  appendIfTruthy(p, 'polish', filters.polish);
  appendIfTruthy(p, 'symmetry', filters.symmetry);
  appendIfTruthy(p, 'minRatio', advancedFilters.minRatio);
  appendIfTruthy(p, 'maxRatio', advancedFilters.maxRatio);
  appendIfTruthy(p, 'minLength', advancedFilters.minLength);
  appendIfTruthy(p, 'maxLength', advancedFilters.maxLength);
  appendIfTruthy(p, 'minWidth', advancedFilters.minWidth);
  appendIfTruthy(p, 'maxWidth', advancedFilters.maxWidth);

  if (page > 0) p.set('page', String(page + 1));
  if (rowsPerPage != null && rowsPerPage !== defaultRows) p.set('limit', String(rowsPerPage));
  const defaultSort = isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT;
  if (sortBy && sortBy !== defaultSort) p.set('sortBy', sortBy);
  if (sortOrder && sortOrder !== 'desc') p.set('sortOrder', sortOrder);

  return p;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lots-route helpers  (/inventory/lots)
// ─────────────────────────────────────────────────────────────────────────────

/** Parse lots filter/pagination from a /inventory/lots search string. */
export function parseLotsSearchParams(searchParams) {
  const get = (k) => searchParams.get(k);

  let page = parseInt(get('page'), 10);
  page = Number.isFinite(page) ? Math.max(0, page - 1) : 0;

  const limit = parseInt(get('limit'), 10);
  const rowsPerPage = Number.isFinite(limit) && LOTS_PAGE_LIMITS.has(limit) ? limit : null;

  return {
    page,
    rowsPerPage,
    shape: get('shape') || '',
    availableOnly: get('show') !== 'all',
    minCaratPerStone: get('minCt') || '',
    maxCaratPerStone: get('maxCt') || '',
    minLength: get('minLen') || '',
    maxLength: get('maxLen') || '',
    minWidth: get('minW') || '',
    maxWidth: get('maxW') || '',
  };
}

/** Build URLSearchParams for /inventory/lots route. */
export function buildLotsSearchParams(snapshot) {
  const {
    page,
    rowsPerPage,
    shape,
    availableOnly,
    minCaratPerStone,
    maxCaratPerStone,
    minLength,
    maxLength,
    minWidth,
    maxWidth,
    defaultRows = 10,
  } = snapshot;

  const p = new URLSearchParams();
  appendIfTruthy(p, 'shape', shape);
  if (!availableOnly) p.set('show', 'all');
  appendIfTruthy(p, 'minCt', minCaratPerStone);
  appendIfTruthy(p, 'maxCt', maxCaratPerStone);
  appendIfTruthy(p, 'minLen', minLength);
  appendIfTruthy(p, 'maxLen', maxLength);
  appendIfTruthy(p, 'minW', minWidth);
  appendIfTruthy(p, 'maxW', maxWidth);
  if (page > 0) p.set('page', String(page + 1));
  if (rowsPerPage != null && rowsPerPage !== defaultRows) p.set('limit', String(rowsPerPage));
  return p;
}
